import type { NodeId } from '../../components/graph-editor/types';
import {
  NodeEvalRequest,
  NodeEvalResult,
  NodeEvalStatus,
} from '../../components/graph-editor/lib/batch/types';
import { extractGraphResult, findEntryResults } from './parse-utils';

type InternalNodeEvalOutput = Record<NodeId, Record<string, unknown>>;

type InternalNodeEvalResponse = {
  output?: InternalNodeEvalOutput | InternalNodeEvalOutput[];
};

const collectNodeOutput = (
  output: NonNullable<InternalNodeEvalResponse['output']>,
  nodeId: NodeId,
): unknown => {
  if (!Array.isArray(output)) {
    return output[nodeId];
  }

  const nodeOutputs = output.flatMap((row) => {
    const value = row[nodeId];
    return value === undefined ? [] : [value];
  });

  if (nodeOutputs.length === 0) return undefined;
  if (nodeOutputs.length === 1) return nodeOutputs[0];
  return nodeOutputs;
};

export const mapGraphEvaluationResponse = (
  entryId: string,
  requests: NodeEvalRequest[],
  response: InternalNodeEvalResponse,
): NodeEvalResult[] => {
  const { output } = response;
  if (!output) return [];

  return requests.map((request) => {
    const nodeOutput = collectNodeOutput(output, request.nodeId);
    if (nodeOutput === undefined) {
      return {
        nodeId: request.nodeId,
        status: NodeEvalStatus.Error,
        message: 'Template did not return a node result',
      };
    }

    if (findEntryResults(nodeOutput, entryId).length === 0) {
      return {
        nodeId: request.nodeId,
        status: NodeEvalStatus.Error,
        message: 'Template did not return an entry result',
      };
    }

    return {
      nodeId: request.nodeId,
      status: NodeEvalStatus.Ok,
      result: extractGraphResult(nodeOutput, entryId),
      fingerprint: '0',
    };
  });
};
